import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { X, ZoomIn } from 'lucide-react';

interface ImageGalleryProps {
  images: string[];
}

const getThumbUrl = (url: string) => {
  if (!url) return url;
  if (url.includes('_thumb.webp')) return url;
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) return url;
  return url.substring(0, lastDotIndex) + '_thumb.webp';
};

const ImageGallery = ({ images }: ImageGalleryProps) => {
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (!images || images.length === 0) return null;

  return (
    <section className="py-20 bg-background border-t border-border/20">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-primary">
            {language === 'PL' ? 'Galeria' : 'Gallery'}
          </h2>
          <div className="h-0.5 w-24 bg-accent mx-auto rounded-full mt-4" />
        </div>

        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative aspect-square overflow-hidden rounded-xl border border-border shadow-sm cursor-pointer bg-muted"
              onClick={() => setSelectedImage(img)}
            >
              <img
                src={getThumbUrl(img)}
                alt={language === 'PL' ? `Zdjęcie w galerii ${index + 1}` : `Gallery image ${index + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/20 group-hover:opacity-100">
                <ZoomIn className="h-8 w-8 text-white drop-shadow-lg" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute right-4 top-4 md:right-8 md:top-8 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20 z-[110]"
              onClick={() => setSelectedImage(null)}
            >
              <X className="h-6 w-6" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage}
              alt="Powiększone zdjęcie galerii"
              className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ImageGallery;
